import {
  INTEGRATION_PROVIDERS,
  SYNC_STATUSES,
  type CustomerIntegrationEntity,
  type ErpIntermediateMetadata,
} from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useUpdateNetSuiteSalesOrder,
  useExportNetSuiteSalesOrder,
} from '../../../../../../../hooks/requests/integrations';
import { useUpdateCustomer } from '../../../../../../../hooks/requests/use-update-customer';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../../utils/vy-mutation';

type UseNetSuiteSyncWizardProps = {
  contractId: string;
  customerId: string;
  isSynced: boolean;
  onClose: () => void;
  needsCustomerConnection: boolean;
  getMemo: () => string;
};

export const useNetSuiteSyncWizard = ({
  contractId,
  customerId,
  isSynced,
  onClose,
  needsCustomerConnection,
  getMemo,
}: UseNetSuiteSyncWizardProps) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [selectedProductsIds, setSelectedProductsIds] = useState<string[]>([]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<ErpIntermediateMetadata | null>(null);

  const updateMutation = useUpdateNetSuiteSalesOrder(contractId);
  const exportMutation = useExportNetSuiteSalesOrder(contractId);
  const exportToNetSuiteMutation = isSynced ? updateMutation : exportMutation;

  const updateCustomerMutation = useUpdateCustomer(customerId);

  const handleNext = useCallback(async () => {
    const isCustomerConnectionStep = needsCustomerConnection && step === 0;

    if (isCustomerConnectionStep && isNotNullOrUndefined(selectedCustomer)) {
      const netSuiteEntity = createNetSuiteEntity(selectedCustomer);

      await runMutation(
        updateCustomerMutation,
        { integrationEntities: [netSuiteEntity] },
        {
          pending: 'Connecting customer...',
          success: 'Customer connected',
          error: 'Failed to connect customer',
        },
      );

      await queryClient.invalidateQueries(queryKeys.contractPage(contractId));
    }

    setStep((prev) => prev + 1);
  }, [needsCustomerConnection, step, selectedCustomer, updateCustomerMutation, queryClient, contractId]);

  const handleBack = useCallback(() => {
    setStep((prev) => prev - 1);
  }, []);

  const handleSync = useCallback(async () => {
    const memo = getMemo();
    onClose();

    await runMutation(
      exportToNetSuiteMutation,
      {
        contractId,
        productsIds: selectedProductsIds,
        subsidiaryId: selectedSubsidiary,
        memo: memo || undefined,
      },
      {
        pending: 'Syncing...',
        success: 'Synced',
        error: 'Failed to sync',
      },
    );
  }, [getMemo, onClose, exportToNetSuiteMutation, contractId, selectedProductsIds, selectedSubsidiary]);

  return {
    step,
    selectedProductsIds,
    setSelectedProductsIds,
    selectedSubsidiary,
    setSelectedSubsidiary,
    selectedCustomer,
    setSelectedCustomer,
    handleNext,
    handleBack,
    handleSync,
  };
};

function createNetSuiteEntity(erpMetadata: ErpIntermediateMetadata): CustomerIntegrationEntity {
  return {
    integrationProvider: INTEGRATION_PROVIDERS.netSuite,
    integrationExternalId: erpMetadata.erpId,
    updatedAt: new Date(),
    syncStatus: SYNC_STATUSES.None,
    metadata: {
      name: erpMetadata.erpName,
      displayId: erpMetadata.displayId,
    },
  };
}
